'use client';

import styled, { css } from 'styled-components';
import { Text, LinkButton } from '@lmn/design-system';
import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';
import { trackViewMap } from '@/app/components/hotel-detail/trackInteractionEvents';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useTranslation } from '@/app/components/translations/Translation';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { SubtleButton } from '@/app/components/hotel-detail/common/button';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { Icon } from '@/app/common/icons/Icon';

const HeroAddressContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  cursor: pointer;
  grid-area: address;
`;

type HeroAddressProps = {
  direction: string;
  isEvo?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export function HeroAddress(props: HeroAddressProps) {
  const { direction, isEvo, ...rest } = props;
  const { t } = useTranslation();
  const { showMap } = useHotelMapModal();
  const { sendCuriosityClickEvent } = useCuriosity();
  const initialParams = useInitialParams();

  const iconSize = isEvo ? 20 : 16;

  const openMap = () => {
    showMap();
    trackViewMap();
    sendCuriosityClickEvent({
      event_name: 'click_view_map_from_address',
      event_context: 'from_address',
    });
  };

  return (
    <HeroAddressContainer onClick={openMap} data-testid="address-button" {...rest}>
      <Icon
        category="Location"
        name="icon-pin-point"
        size={iconSize}
        color="iconSecondary"
        alt={t('vacation.oss.comparison.modal.address')}
        css={css`
          margin-right: 8px;

          @media ${customCss.breakpoints.sm} {
            margin-top: 0;
            align-items: center;
          }
        `}
      />
      <span>
        <Text
          variant="BodySmall"
          forceTag="span"
          css={`
            flex-grow: 1;
          `}
        >
          {direction}
        </Text>
        {initialParams.isHotelStaticPage ? undefined : (
          <span
            css={`
              padding: 0 8px;
            `}
          >
            ·
          </span>
        )}

        {initialParams.isHotelStaticPage ? undefined : isEvo ? (
          <SubtleButton
            onClick={openMap}
            onKeyUp={keyUpHandler(openMap)}
            data-testid="open-map"
            css={css`
              color: ${(p) => p.theme.commons.colors.gray400};
            `}
          >
            <Text variant="BodySmall">{t('vacation.select.details.product.quickinfo.viewmap')}</Text>
          </SubtleButton>
        ) : (
          <LinkButton
            variant="accent"
            onClick={openMap}
            onKeyUp={keyUpHandler(openMap)}
            style={{ whiteSpace: 'nowrap' }}
            css={css`
              ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
            `}
          >
            {t('vacation.select.details.product.quickinfo.viewmap')}
          </LinkButton>
        )}
      </span>
    </HeroAddressContainer>
  );
}
